import type { CoachingSession } from '@efdigital/ll-addon-data';
import type CoachService from '@efdigital/ll-xceed-v2/services/coach';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface ConfirmBookingModalSignature {
  Args: {
    session: CoachingSession;
    onChanged?: () => void | Promise<void>;
  };
  Blocks: {
    default: [showModal: () => void];
  };
}

export class ConfirmBookingModal extends Component<ConfirmBookingModalSignature> {
  @service declare coach: CoachService;
  @tracked open = false;
  @tracked loading = false;
  @tracked errorMessage = '';

  closeModal = () => {
    this.open = false;
  };

  confirmBooking = async () => {
    this.loading = true;
    this.errorMessage = '';
    try {
      await this.coach.confirmSession(this.args.session);
      await this.args.onChanged?.();
      this.open = false;
    } catch {
      this.errorMessage = 'Could not accept this booking. Please try again.';
    } finally {
      this.loading = false;
    }
  };

  showModal = () => {
    this.errorMessage = '';
    this.open = true;
  };

  <template>
    {{#if this.open}}
      <BsModal @open={{this.open}} as |modal|>
        <modal.header>
          <h5 class="modal-title">Accept booking</h5>
        </modal.header>
        <modal.body aria-busy={{this.loading}}>
          <p>Please confirm your acceptance of this booking. The coachee will be
            notified and the session will be added to your schedule.</p>
          {{#if this.errorMessage}}
            <div class="alert alert-danger mb-0" role="alert">
              {{this.errorMessage}}
            </div>
          {{/if}}
        </modal.body>
        <modal.footer>
          <button
            class="btn btn-outline-primary"
            type="button"
            {{on "click" this.closeModal}}
            disabled={{this.loading}}
          >Decide later</button>
          <button
            class="btn btn-primary"
            type="button"
            {{on "click" this.confirmBooking}}
            disabled={{this.loading}}
            aria-busy={{this.loading}}
          >Accept booking</button>
        </modal.footer>
      </BsModal>
    {{/if}}
    {{yield this.showModal}}
  </template>
}
